import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import OfferCard from "./OfferCard";
import WelcomeHeader from "./WelcomeHeader";
import { Tag, Calendar, ChevronRight } from "lucide-react";
import { supabase } from "../utils/supabase";

const OffersPageNew = ({ userProfile = {} }) => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");

  useEffect(() => {
    fetchOffers();
    fetchCategories();
  }, []);

  const fetchOffers = async () => {
    try {
      const { data, error } = await supabase
        .from("offers")
        .select(
          `
          *,
          offers_categories (
            id,
            category_name,
            emoji
          )
        `
        )
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching offers:", error);
        setError("Error al cargar las ofertas");
      } else {
        const transformedOffers = (data || []).map((offer) => ({
          id: offer.id,
          title: offer.title,
          description: offer.description,
          image:
            offer.image_url ||
            "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600&fit=crop",
          type: "descuento",
          category: offer.offers_categories?.category_name || "Sin categoría",
          categoryEmoji: offer.offers_categories?.emoji || "🏷️",
          category_id: offer.category_id,
          price: offer.price ? parseFloat(offer.price) : undefined,
          originalPrice: offer.original_price
            ? parseFloat(offer.original_price)
            : undefined,
          discount: offer.discount,
          start_date: offer.start_date,
          end_date: offer.end_date,
        }));
        setOffers(transformedOffers);
      }
    } catch (error) {
      console.error("Error fetching offers:", error);
      setError("Error al cargar las ofertas");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("offers_categories")
        .select("id, category_name, emoji")
        .eq("is_active", true)
        .order("category_name", { ascending: true });

      if (error) {
        console.error("Error fetching categories:", error);
      } else {
        setCategories(data || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const filteredOffers = offers.filter((offer) => {
    if (filterCategory === "all") {
      return true; // Mostrar todas las ofertas para "Recomendado"
    }
    // Filtrar por category_id
    return offer.category_id === filterCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando ofertas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 pt-24 px-4 pb-8">
      <div className="max-w-7xl mx-auto space-y-8 mt-16">
        {/* Header */}
        {userProfile && Object.keys(userProfile).length > 0 && (
          <WelcomeHeader userProfile={userProfile} />
        )}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mt-6 text-center"
        >
          <button
            onClick={() => navigate("/eventos")}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-full hover:from-purple-700 hover:to-pink-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Ver Próximos Eventos
            <ChevronRight className="w-5 h-5 ml-2" />
          </button>
        </motion.div>

        {/* Filtros por categoría */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="max-w-4xl mx-auto mb-12"
        >
          <div className="flex flex-wrap justify-center gap-3">
            {/* Botón "Recomendado" para mostrar todas las ofertas */}
            <button
              onClick={() => setFilterCategory("all")}
              className={`px-6 py-2 rounded-full border transition-all duration-300 ${
                filterCategory === "all"
                  ? "bg-orange-500 text-white border-orange-500 shadow-lg"
                  : "bg-white text-gray-700 border-gray-300 hover:border-orange-300 hover:bg-orange-50"
              }`}
            >
              😍 Recomendado
            </button>

            {/* Botones de categorías dinámicas */}
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setFilterCategory(category.id)}
                className={`px-6 py-2 rounded-full border transition-all duration-300 ${
                  filterCategory === category.id
                    ? "bg-orange-500 text-white border-orange-500 shadow-lg"
                    : "bg-white text-gray-700 border-gray-300 hover:border-orange-300 hover:bg-orange-50"
                }`}
              >
                {category.emoji} {category.category_name}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Grid de ofertas */}
        {error ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center py-12"
          >
            <Tag className="w-16 h-16 text-red-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-red-600 mb-2">
              Error al cargar ofertas
            </h3>
            <p className="text-red-500">{error}</p>
          </motion.div>
        ) : filteredOffers.length > 0 ? (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {filteredOffers.map((offer, index) => (
              <OfferCard key={offer.id} offer={offer} index={index} />
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center py-12"
          >
            <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              No hay ofertas disponibles
            </h3>
            <p className="text-gray-500">
              {filterCategory !== "all"
                ? "No hay ofertas en esta categoría. ¡Prueba con otra categoría!"
                : "No hay ofertas disponibles en este momento. ¡Vuelve pronto!"}
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default OffersPageNew;
